import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PageTitle, Card, Alert } from '../components';
import { FaUser, FaLock, FaUserEdit, FaEnvelope, FaKey, FaCheckCircle } from 'react-icons/fa';

const ProfilePage = () => {
  const { currentUser, updateProfile, updatePassword } = useAuth();
  
  // 个人资料状态
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
  });
  
  // 密码状态
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  // 表单提交状态
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // 初始化表单数据
  useEffect(() => {
    if (currentUser) {
      setProfileData({
        name: currentUser.name || '',
        email: currentUser.email || '',
      });
    }
  }, [currentUser]);

  // 处理个人资料变更
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // 处理密码变更
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // 提交个人资料表单
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setProfileLoading(true);
      setProfileError('');
      setProfileSuccess(false);
      
      // 验证表单数据
      if (!profileData.name.trim()) {
        return setProfileError('姓名不能为空');
      }
      
      // 调用更新资料API
      await updateProfile({
        name: profileData.name,
      });
      
      setProfileSuccess(true);
      
      // 3秒后隐藏成功提示
      setTimeout(() => {
        setProfileSuccess(false);
      }, 3000);
    } catch (err) {
      setProfileError(err.response?.data?.message || '更新资料失败，请稍后再试');
    } finally {
      setProfileLoading(false);
    }
  };

  // 提交密码表单
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setPasswordLoading(true);
      setPasswordError('');
      setPasswordSuccess(false);
      
      // 验证密码
      if (!passwordData.currentPassword) {
        return setPasswordError('请输入当前密码');
      }
      
      if (passwordData.newPassword.length < 6) {
        return setPasswordError('新密码长度至少为6位');
      }
      
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        return setPasswordError('两次输入的新密码不一致');
      }
      
      // 调用更新密码API
      await updatePassword(passwordData.currentPassword, passwordData.newPassword);
      
      // 清空密码表单
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
      setPasswordSuccess(true);
      
      // 3秒后隐藏成功提示
      setTimeout(() => {
        setPasswordSuccess(false);
      }, 3000);
    } catch (err) {
      setPasswordError(err.response?.data?.message || '更新密码失败，请检查当前密码是否正确');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="profile-page">
      <PageTitle 
        title="个人资料"
        subtitle="查看和更新您的账号信息"
        breadcrumbs={[
          { text: '仪表盘', link: '/dashboard' },
          { text: '个人资料', link: '/profile' }
        ]}
      />
      
      <div className="row">
        {/* 个人资料卡片 */}
        <div className="col-md-6 mb-4">
          <Card 
            title="个人信息" 
            icon={<FaUser className="text-primary" />}
          >
            {profileSuccess && (
              <Alert 
                type="success"
                message="个人资料更新成功！"
                autoHideDuration={3000}
              />
            )}
            
            {profileError && (
              <Alert 
                type="danger"
                message={profileError}
                dismissible={true}
                onClose={() => setProfileError('')}
              />
            )}
            
            <form onSubmit={handleProfileSubmit}>
              {/* 用户名 */}
              <div className="mb-3">
                <label htmlFor="name" className="form-label">姓名</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaUserEdit />
                  </span>
                  <input 
                    type="text" 
                    className="form-control" 
                    id="name" 
                    name="name"
                    value={profileData.name}
                    onChange={handleProfileChange}
                    disabled={profileLoading}
                    required
                  />
                </div>
              </div>
              
              {/* 邮箱地址 - 只读，不可修改 */}
              <div className="mb-4">
                <label htmlFor="email" className="form-label">邮箱地址</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaEnvelope />
                  </span>
                  <input 
                    type="email" 
                    className="form-control" 
                    id="email" 
                    name="email"
                    value={profileData.email}
                    disabled={true}
                    readOnly
                  />
                </div>
                <small className="form-text text-muted">邮箱地址不可修改</small>
              </div>
              
              {/* 提交按钮 */}
              <div className="d-grid">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={profileLoading}
                >
                  {profileLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      更新中...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle className="me-2" />
                      保存修改
                    </>
                  )}
                </button>
              </div>
            </form>
          </Card>
        </div>
        
        {/* 修改密码卡片 */}
        <div className="col-md-6 mb-4">
          <Card 
            title="修改密码" 
            icon={<FaLock className="text-primary" />}
          >
            {passwordSuccess && (
              <Alert 
                type="success"
                message="密码修改成功！"
                autoHideDuration={3000}
              />
            )}
            
            {passwordError && (
              <Alert 
                type="danger"
                message={passwordError}
                dismissible={true}
                onClose={() => setPasswordError('')}
              />
            )}
            
            <form onSubmit={handlePasswordSubmit}>
              {/* 当前密码 */}
              <div className="mb-3">
                <label htmlFor="currentPassword" className="form-label">当前密码</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaKey />
                  </span>
                  <input 
                    type="password" 
                    className="form-control" 
                    id="currentPassword" 
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    disabled={passwordLoading}
                    required
                  />
                </div>
              </div>
              
              {/* 新密码 */}
              <div className="mb-3">
                <label htmlFor="newPassword" className="form-label">新密码</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaLock />
                  </span>
                  <input 
                    type="password" 
                    className="form-control" 
                    id="newPassword" 
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    disabled={passwordLoading}
                    required
                  />
                </div>
                <small className="form-text text-muted">密码长度至少为6位</small>
              </div>
              
              {/* 确认新密码 */}
              <div className="mb-4">
                <label htmlFor="confirmPassword" className="form-label">确认新密码</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaCheckCircle />
                  </span>
                  <input 
                    type="password" 
                    className="form-control" 
                    id="confirmPassword" 
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    disabled={passwordLoading}
                    required
                  />
                </div>
              </div>
              
              {/* 提交按钮 */}
              <div className="d-grid">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      更新中...
                    </>
                  ) : (
                    <>
                      <FaKey className="me-2" />
                      更新密码
                    </>
                  )}
                </button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage; 